import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Modal, Button, Row, Col, Divider } from 'antd';
import { Form, Input } from 'formik-antd';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { isNil, toNumber } from 'lodash';

import {
  statesSelector,
  citiesSelector,
  isCitiesLoadingSelector,
  isStatesLoadingSelector,
  countriesSelector,
  isCountriesLoadingSelector,
  isCountryCitiesLoadingSelector,
  countryCitiesSelector,
} from 'redux/selectors/regionSelectors';
import FormItem from 'components/formik/FormItem';
import BaseAlert from 'components/wrappers/BaseAlert';
import InputNumber from 'components/formik/InputNumber';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import SubmitButton from 'components/formik/SubmitButton';
import { createPartner } from 'redux/reducers/partnerSlice';
import { CreatePartnerModel } from 'api/backofficeApiClient';
import useModalCloseOnNavigate from 'hooks/useModalCloseOnNavigate';
import {
  getAllStates,
  getAllCities,
  getAllCountries,
  getAllCountryCities,
} from 'redux/reducers/regionSlice';
import { BaseModalFooter, BaseModal } from 'components/wrappers/BaseModal';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import { countryUSAid } from 'constants/index';

const maxCharactersLength = 120;
const partnerCreatedMessage = 'Partner created successfully.';

type Props = {
  visible: boolean;
  onClose: () => void;
};

export interface PartnerModalFormValues {
  companyName: string;
  subcontractorRate: number | null;
  country?: number;
  state: string;
  internationalStateName?: string;
  internationalCityName?: string;
  city: string;
  address: string;
  zip: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const validationSchema = Yup.object({
  companyName: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxCharactersLength,
      `Company name should be ${maxCharactersLength} characters maximum`,
    ),
  subcontractorRate: Yup.number()
    .nullable()
    .min(0, 'Subcontractor rate should be from 0 to 999999')
    .max(999999, 'Subcontractor rate should be from 0 to 999999'),
  country: Yup.number().nullable().required('Field is required'),
  state: Yup.string().when('country', {
    is: (country: number) => checkIsCountriesWithStatesDropdown(country),
    then: Yup.string().nullable().required('Field is required'),
  }),
  internationalStateName: Yup.string().when('country', {
    is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
    then: Yup.string()
      .trim()
      .max(120, 'State should be 120 characters maximum')
      .required('Field is required'),
  }),
  city: Yup.string().when('country', {
    is: (country: number) => checkIsCountriesWithStatesDropdown(country),
    then: Yup.string().nullable().required('Field is required'),
  }),
  internationalCityName: Yup.string().when('country', {
    is: (country: number) => !checkIsCountriesWithStatesDropdown(country),
    then: Yup.string()
      .max(120, 'City should be 120 characters maximum')
      .required('Field is required'),
  }),
  address: Yup.string()
    .trim()
    .required('Field is required')
    .max(120, `Address should be 120 characters maximum`),
  zip: Yup.string()
    .trim()
    .max(120, 'ZIP code should be 120 characters maximum'),

  firstName: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxCharactersLength,
      `First name should be ${maxCharactersLength} characters maximum`,
    ),
  lastName: Yup.string()
    .trim()
    .required('Field is required')
    .max(
      maxCharactersLength,
      `Last name should be ${maxCharactersLength} characters maximum`,
    ),
  email: Yup.string()
    .trim()
    .email('Email format is invalid')
    .required('Field is required')
    .max(
      maxCharactersLength,
      `Email should be ${maxCharactersLength} characters maximum`,
    ),
  phone: Yup.string()
    .trim()
    .required('Field is required')
    .max(120, 'Phone number should be 120 characters maximum'),
});

export default function PartnerModal(props: Props) {
  const { visible, onClose } = props;
  const dispatch = useAppDispatch();
  const formRef: any = useRef();
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);
  const countryCities = useAppSelector(countryCitiesSelector);
  const [selectedCountry, setSelectedCountry] = useState<number>(countryUSAid);
  const [selectedState, setSelectedState] = useState<number>();

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === selectedCountry),
    [selectedCountry, states],
  );

  const filteredCities = useMemo(
    () => cities.filter((city) => city.stateId === selectedState),
    [selectedState, cities],
  );

  const filteredInternationalCities = useMemo(
    () => countryCities.filter((city) => city.countryId === selectedCountry),
    [countryCities, selectedCountry],
  );

  useModalCloseOnNavigate(onClose);

  const initialValues: PartnerModalFormValues = {
    companyName: '',
    subcontractorRate: null,
    country: countryUSAid,
    state: '',
    internationalStateName: undefined,
    city: '',
    internationalCityName: undefined,
    address: '',
    zip: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  };

  useEffect(() => {
    dispatch(getAllStates());
    dispatch(getAllCities());
    dispatch(getAllCountries());
    dispatch(getAllCountryCities());
  }, [dispatch]);

  const handleStateChange = (
    stateId: number,
    formik: FormikProps<PartnerModalFormValues>,
  ) => {
    setSelectedState(stateId);
    if (checkIsCountriesWithStatesDropdown(selectedCountry)) {
      formik.setFieldValue('city', '', false);
    } else {
      formik.setFieldValue('internationalCityName', '', false);
    }
  };

  const handleSubmit = async (values: PartnerModalFormValues) => {
    const {
      companyName,
      subcontractorRate,
      state,
      city,
      address,
      zip,
      firstName,
      lastName,
      email,
      phone,
      country,
      internationalStateName,
      internationalCityName,
    } = values;
    const data: CreatePartnerModel = {
      primaryContact: {
        firstName,
        lastName,
        email,
        phoneNumber: phone,
      },
      generalInfo: {
        companyName: companyName,
        subcontractorRate: !isNil(subcontractorRate)
          ? subcontractorRate
          : undefined,
        countryId: !isNil(country) ? country : countryUSAid,
        internationalCityName: internationalCityName || undefined,
        internationalStateName: internationalStateName || undefined,
        stateId: toNumber(state),
        city: city,
        address: address,
        zipCode: zip || undefined,
      },
    };

    const result = await dispatch(createPartner(data));

    if (createPartner.fulfilled.match(result)) {
      BaseAlert.success(partnerCreatedMessage);

      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current.dirty) {
      return Modal.confirm({
        title: 'Changes won’t be saved. Are you sure?',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<PartnerModalFormValues>,
  ) => {
    setSelectedCountry(countryId);
    formik.setFieldValue('city', '', false);
    formik.setFieldValue('state', undefined, false);
    formik.setFieldValue('internationalCityName', '', false);
    formik.setFieldValue('internationalStateName', '', false);
  };

  return (
    <BaseModal
      title="Create new partner"
      open={visible}
      closable={true}
      onCancel={handleCloseModal}
      destroyOnClose
      maxWidth={889}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        {(formik) => (
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div className="modal-title">General information</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <FormItem
                    data-id="companyNameField"
                    label="Company name *"
                    name="companyName"
                  >
                    <Input
                      name="companyName"
                      placeholder="Enter company name"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem
                        label="Subcontractor rate"
                        name="subcontractorRate"
                      >
                        <InputNumber
                          name="subcontractorRate"
                          placeholder="Enter subcontractor rate"
                          className="full-width"
                          precision={2}
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row gutter={40}>
                <Col xs={24} md={6}>
                  <FormItem
                    name="country"
                    label="Country *"
                    data-id="newPartnerCountryField"
                  >
                    <FormikSelectWithLoading
                      isLoading={isCountriesLoading}
                      name="country"
                      showSearch
                      placeholder="Select country"
                      optionFilterProp="children"
                      onChange={(value: number) =>
                        handleCountryChange(value, formik)
                      }
                    >
                      {countries.map((country) => (
                        <FormikSelectWithLoading.Option
                          key={`select-country-${country.id}`}
                          value={country.id || -1}
                        >
                          {country.name}
                        </FormikSelectWithLoading.Option>
                      ))}
                    </FormikSelectWithLoading>
                  </FormItem>
                </Col>
                {checkIsCountriesWithStatesDropdown(formik.values.country) && (
                  <Col xs={24} md={6}>
                    <FormItem name="state" label="State *">
                      <FormikSelectWithLoading
                        isLoading={isStatesLoading}
                        name="state"
                        showSearch
                        placeholder="Select state"
                        optionFilterProp="children"
                        onChange={(value: number) =>
                          handleStateChange(value, formik)
                        }
                      >
                        {filteredStates.map((state) => (
                          <FormikSelectWithLoading.Option
                            key={`select-state-${state.id}`}
                            value={state.id || -1}
                          >
                            {state.name}
                          </FormikSelectWithLoading.Option>
                        ))}
                      </FormikSelectWithLoading>
                    </FormItem>
                  </Col>
                )}

                {!checkIsCountriesWithStatesDropdown(formik.values.country) && (
                  <Col xs={24} md={6}>
                    <FormItem name="internationalStateName" label="State *">
                      <Input
                        name="internationalStateName"
                        placeholder="Enter state"
                        disabled={!formik.values.country}
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>
                )}

                {checkIsCountriesWithStatesDropdown(formik.values.country) && (
                  <Col xs={24} md={6}>
                    <FormItem name="city" label="City *">
                      <FormikAutoCompleteWithLoading
                        isLoading={isCitiesLoading}
                        name="city"
                        filterOption
                        disabled={!formik.values.state}
                        placeholder="Enter city"
                        showSearch
                      >
                        {filteredCities.map((city) => (
                          <FormikAutoCompleteWithLoading.Option
                            key={`city-state-${city.id}`}
                            value={city.name || ''}
                          >
                            {city.name}
                          </FormikAutoCompleteWithLoading.Option>
                        ))}
                      </FormikAutoCompleteWithLoading>
                    </FormItem>
                  </Col>
                )}

                {!checkIsCountriesWithStatesDropdown(formik.values.country) && (
                  <Col xs={24} md={6}>
                    <FormItem name="internationalCityName" label="City *">
                      <FormikAutoCompleteWithLoading
                        isLoading={isCountryCitiesLoading}
                        name="internationalCityName"
                        filterOption
                        disabled={!formik.values.internationalStateName}
                        placeholder="Enter city"
                        showSearch
                      >
                        {filteredInternationalCities.map((city) => (
                          <FormikAutoCompleteWithLoading.Option
                            key={`city-state-${city.id}`}
                            value={city.name || ''}
                          >
                            {city.name}
                          </FormikAutoCompleteWithLoading.Option>
                        ))}
                      </FormikAutoCompleteWithLoading>
                    </FormItem>
                  </Col>
                )}
                <Col xs={24} md={6}>
                  <FormItem label="Address *" name="address">
                    <Input name="address" placeholder="Enter address" autoComplete="off" />
                  </FormItem>
                </Col>
                <Col xs={24} md={6}>
                  <FormItem label="ZIP" name="zip">
                    <Input
                      name="zip"
                      className="full-width"
                      placeholder="Enter ZIP code"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>
              </Row>

              <Divider className="modal-divider after-form" />

              <div className="modal-title">Primary contact</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem
                        data-id="firstNameField"
                        label="First name *"
                        name="firstName"
                      >
                        <Input
                          name="firstName"
                          placeholder="Enter first name"
                          autoComplete="off"
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem
                        data-id="lastNameField"
                        label="Last name *"
                        name="lastName"
                      >
                        <Input name="lastName" placeholder="Enter last name" autoComplete="off" />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem
                        data-id="emailField"
                        label="Email *"
                        name="email"
                      >
                        <Input name="email" placeholder="Enter email" autoComplete="off" />
                      </FormItem>
                    </Col>

                    <Col xs={24} md={12}>
                      <FormItem label="Phone number *" name="phone">
                        <Input name="phone" placeholder="Enter phone number" autoComplete="off" />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>

            <BaseModalFooter>
              <div>
                <Button
                  data-id="closePartnerModalBtn"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>

                <SubmitButton type="primary" data-id="submitPartnerBtn">
                  Create
                </SubmitButton>
              </div>
            </BaseModalFooter>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
}
